import Dropdown from "@components/Dropdown";
import classNames from "classnames";
import React, { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { roles } from "@const/livestreams";
import { useTranslation } from "react-i18next";
import {
  banParticipant,
  changeParticipantRole,
  deleteAllParticipantMessages,
  deleteMessage,
  muteParticipant,
} from "@redux/livestreams/actions";
import { DropdownItem } from "reactstrap";
import { ReactComponent as IconShowMore } from "@assets/ic-show-more.svg";
import styles from "../index.module.scss";
import { useService } from "@/base/hooks/useService";
import { ToasterService } from "@services/ToastService";

const DropdownItemsModerator = ({
  isMyMessage,
  streamParticipant,
  isMeModerator,
  hideDeleteAllMessages,
}) => {
  const { t } = useTranslation("errors");
  const { t: t1 } = useTranslation("livestream");
  const toastService = useService(ToasterService);
  const dispatch = useDispatch();

  const role = streamParticipant?.role;
  const id = streamParticipant?.id;

  const isUserModerator = role === roles.moderator;

  const isOnStream = streamParticipant?.isOnStream;

  const handleChangeModeratorPermissions = () => {
    const newRole = isUserModerator ? roles.viewer : roles.moderator;
    dispatch(
      changeParticipantRole({
        id,
        body: { role: newRole },
        errorCallback: () => {
          toastService.error(t("CANT_ASSIGN_MODERATOR"));
        },
      }),
    );
  };

  const handleDeleteAllParticipantMessages = () => {
    dispatch(deleteAllParticipantMessages({ id }));
  };

  const handleBanParticipant = () => {
    dispatch(
      banParticipant({
        ...streamParticipant,
        errorCallback: () => {
          toastService.error(t("CANT_BAN_USER"));
        },
      }),
    );
  };

  const handleMuteParticipant = () => {
    dispatch(
      muteParticipant({
        id,
        errorCallback: () => {
          toastService.error(t("CANT_MUTE_USER"));
        },
      }),
    );
  };

  return (
    <>
      {!isMyMessage && !isMeModerator && isOnStream && (
        <DropdownItem onClick={handleChangeModeratorPermissions}>
          {isUserModerator ? t1("REVOKE") : t1("APPOINT")}
        </DropdownItem>
      )}
      {isOnStream && (
        <DropdownItem onClick={handleMuteParticipant}>
          {t1("MUTE_FOR_FIVE")}
        </DropdownItem>
      )}
      {!hideDeleteAllMessages && (
        <DropdownItem onClick={handleDeleteAllParticipantMessages}>
          {t1("DELETE_ALL_MESSAGES")}
        </DropdownItem>
      )}
      {!isMyMessage && isOnStream && (
        <DropdownItem onClick={handleBanParticipant}>
          {t1("BAN_USER")}
        </DropdownItem>
      )}
    </>
  );
};

const DropdownItemsViewer = ({ messageId }) => {
  const { t } = useTranslation("buttons");
  const dispatch = useDispatch();
  const handleDeleteMessage = () => {
    dispatch(deleteMessage({ id: messageId }));
  };

  return (
    <>
      <DropdownItem onClick={handleDeleteMessage}>{t("DELETE")}</DropdownItem>
    </>
  );
};

const ChatActionDropdown = ({
  streamParticipant,
  messageId = "",
  author,
  hideDeleteAllMessages,
  togglerClassName = "chat-action-toggler-icon",
}) => {
  const dropDownRef = useRef(null);
  const { id } = useSelector(({ profileReducer }) => profileReducer);
  const { activeStream } = useSelector(
    ({ livestreamsReducer }) => livestreamsReducer,
  );

  const isHostMessage = author?.id === activeStream?.author?.id;

  const role = activeStream?.streamParticipant?.role;

  const isMyMessage = author?.id === id;

  const isMeModerator = role === roles.moderator;
  const isMeCreator = role === roles.creator;
  const isMeViewer = role === roles.viewer;

  if ((isHostMessage && !isMyMessage) || streamParticipant?.isBanned) {
    return null;
  }

  const isDropDownVisible =
    (isMeViewer && isMyMessage) || isMeModerator || isMeCreator;

  return isDropDownVisible ? (
    <Dropdown
      end
      flip
      direction="up"
      ref={dropDownRef}
      usePortal
      toggler={
        <IconShowMore
          className={classNames("text-tertiary", togglerClassName)}
        />
      }
      togglerClassName="btn-resetStyle z-5"
      menuClassName={classNames("w-auto", styles.chatActionDropDownMenu)}
      wrapperClassName="chat-action-dots"
    >
      {(isMeModerator || isMeCreator || isMyMessage) && messageId ? (
        <DropdownItemsViewer messageId={messageId} />
      ) : null}
      {(isMeModerator || isMeCreator) && !isMyMessage ? (
        <DropdownItemsModerator
          streamParticipant={streamParticipant}
          isMyMessage={isMyMessage}
          isMeModerator={isMeModerator}
          hideDeleteAllMessages={hideDeleteAllMessages}
        />
      ) : null}
    </Dropdown>
  ) : null;
};

export default ChatActionDropdown;
